<template>
  <div>
    <div class="section section--half-y-padding">
      <h2 class="title title--2 quarter-spacing">
        {{ content.PayAtTableOrderSummary.title }}
      </h2>

      <p v-if="content.PayAtTableOrderSummary.description">
        {{ content.PayAtTableOrderSummary.description }}
      </p>
    </div>

    <validated-form
      v-if="order.orderItems.length === 0 && order.orderItemsStaged.length === 0"
      name="emptyCart"
      @valid-submit="findOrder"
    >
      <div class="section section--alt-background center">
        <p class="title title--4">
          Your cart is currently empty.
        </p>

        <p>
          Browse our menu and start adding some tasty items to your cart.
        </p>

        <button
          class="button button--primary button--large button--auto-width"
          type="button"
          @click="$closeModal('cart')"
        >
          Browse Menu
        </button>
      </div>

      <div class="section center">
        <template v-if="tableNumberDisplay && !isFindingOrder">
          <p class="title title--6 no-spacing">
            Your {{ $lowercase(userLocationType.description) }} number is:
          </p>

          <p class="title title--2">
            {{ tableNumberDisplay }}
          </p>
        </template>

        <template v-else>
          <p class="title title--4">
            Already have an open order?
          </p>

          <p>
            Enter your {{ $lowercase(userLocationType.description) }} number to load your order:
          </p>

          <div class="input-with-button justify-center">
            <div class="input-with-button__input input-with-button__input--auto-width">
              <text-input
                :model-value="manuallyEnteredTableNumber"
                type="text"
                auto-width
                :label="`${userLocationType.description} #`"
                :icon-class="userLocationType.icon || 'fa-utensils'"
                name="manuallyEnteredTableNumber"
                :validations="{
                  required: true,
                  alpha_num: true,
                }"
                @update:model-value="handleTableNumberInput"
              />
            </div>

            <div class="input-with-button__button">
              <button
                class="button button--secondary button--large"
                :class="{
                  'button--loading': isFindingOrder
                }"
                type="submit"
              >
                Find Order
              </button>
            </div>
          </div>
        </template>

        <p
          v-if="noExistingOrderFound"
          class="message animation-grow-in"
        >
          There are no open orders for your {{ $lowercase(userLocationType.description) }}
          <template v-if="userLocationType.type === 'Room'">
            for your room.
          </template>
          <template v-else>
            at your table.
          </template>
          Add items to your cart to start a new order.
        </p>
      </div>
    </validated-form>

    <validated-form
      v-else
      name="cart"
      @valid-submit="submitOrder"
    >
      <div
        v-if="order.orderItemsStaged.length > 0"
        class="section"
      >
        <h3 class="title title--6">
          <span v-if="order.orderItems.length > 0">Added To</span>
          Your Order
        </h3>

        <order-items
          :hide-totals="true"
          :order="order"
          :order-items="order.orderItemsStaged"
          :outside-borders="true"
        />

        <div
          v-if="order.orderItems.length === 0"
          class="key-value"
        >
          <dl class="key-value__row key-value__row--large">
            <dt class="key-value__key">
              Subtotal
            </dt>
            <dd class="key-value__value">
              {{ $dollars(order.subtotalIncludingStagedItems) }}
            </dd>
          </dl>
        </div>
      </div>

      <upsell-order-items />

      <div
        v-if="order.orderItemsStaged.length > 0"
        class="section section--alt-background center"
      >
        <template v-if="tableNumberDisplay">
          <p class="title title--6 no-spacing">
            Your {{ $lowercase(userLocationType.description) }} number is:
          </p>

          <p class="title title--2">
            {{ tableNumberDisplay }}
          </p>
        </template>

        <template v-else>
          <p class="title title--6">
            Enter your {{ $lowercase(userLocationType.description) }} number below:
          </p>

          <text-input
            :model-value="manuallyEnteredTableNumber"
            type="text"
            auto-width
            :label="`${userLocationType.description} #`"
            class="auto-spacing-left auto-spacing-right"
            :icon-class="userLocationType.icon || 'fa-utensils'"
            name="manuallyEnteredTableNumber"
            :validations="{
              required: true,
              alpha_num: true,
            }"
            @update:model-value="handleTableNumberInput"
          />
        </template>

        <payment-methods
          v-if="supportsOpenTab && order.orderItemCount === 0"
          primary-cta-text="Send Order To Kitchen"
          :pay-cta-text="content.OpenTabPaymentDetails.metadata.cta"
          :payment-modal-title="content.OpenTabPaymentDetails.title"
          :hidden-elements="{
            contactInfo: true,
            total: true
          }"
          is-pre-auth
          :total="tabHoldAmount"
          @checkout="submitPaymentInfo"
        >
          <template #payment-modal-top>
            <div class="section section--half-y-padding">
              <p
                v-if="content.OpenTabPaymentDetails.metadata.subtitle.includes('{{defaultOpenTabAmount}}')"
                v-html="openTabDefaultSubtitleHtml"
              />
              <p v-else>{{ content.OpenTabPaymentDetails.metadata.subtitle }}</p>
            </div>
          </template>
        </payment-methods>

        <button
          v-else
          class="button button--large button--primary button--auto-width"
          :class="{
            'button--loading': isSubmittingOrder
          }"
          type="submit"
        >
          Send Order To Kitchen
        </button>
      </div>

      <div
        v-else-if="order.orderItems.length > 0"
        class="section section--alt-background center"
      >
        <p class="title title--6 no-spacing">
          Your {{ $lowercase(userLocationType.description) }} number is:
        </p>

        <p class="title title--2">
          {{ tableNumberDisplay }}
        </p>

        <button
          class="button button--large button--primary button--auto-width"
          type="button"
          @click="goToCheckoutPage"
        >
          Checkout
        </button>
      </div>

      <div
        v-if="order.orderItems.length > 0"
        class="section"
      >
        <div class="spacing">
          <h3 class="title title--6">
            Existing Cart
          </h3>

          <order-items
            :hide-quantity="true"
            :hide-totals="true"
            :order="order"
            :outside-borders="true"
            :read-only="true"
          />

          <div class="key-value">
            <dl class="key-value__row key-value__row--large">
              <dt class="key-value__key">
                Subtotal
              </dt>
              <dd class="key-value__value">
                {{ $dollars(order.subtotalIncludingStagedItems) }}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </validated-form>
  </div>
</template>


<script>
  import errorHandlers from '@/services/error-handlers';
  import pay from '@/services/pay';
  import events from '@/services/events';

  import { mapGetters } from 'vuex';
  import formatUsd from 'format-usd';

  export default {
    name: 'TableCart',

    data() {
      return {
        isFindingOrder: false,
        isSubmittingOrder: false,
        manuallyEnteredTableNumber: null,
        noExistingOrderFound: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('orders', ['order', 'tableNumberDisplay', 'userLocationType']),
      ...mapGetters('session', ['session']),
      ...mapGetters('merchant', ['merchant']),

      tabHoldAmount() {
        return this.selectedLocation.defaultTabAmount;
      },

      openTabDefaultSubtitleHtml() {
        const holdAmount = this.order.subtotalIncludingStagedItems > this.tabHoldAmount
          ? this.order.subtotalIncludingStagedItems
          : this.tabHoldAmount;

        return this.content.OpenTabPaymentDetails.metadata.subtitle.replace(
          '{{defaultOpenTabAmount}}',
          `<span class="bold">${formatUsd({ amount: holdAmount })}</span>`
        );
      },

      supportsOpenTab() {
        return this.selectedLocation.features.OpenTab
          && (
            !this.order.orderId /* no order exists yet because no table number has been specified */
            || this.order.features?.includes('OpenTab') /* an order does exist and it supports OpenTab */
          );
      }
    },

    methods: {
      handleTableNumberInput(value) {
        this.manuallyEnteredTableNumber = value.toUpperCase();
      },

      async findOrder({ checkoutNow = true } = {}) {
        try {
          this.isFindingOrder = true;

          this.$store.dispatch('session/updateRouteParamsDirectly', {
            tableNumber: this.manuallyEnteredTableNumber
          });

          await this.$store.dispatch('orders/getOrderForOrderAtTable', { silent: true, throw: true });

          if (this.order.orderItems.length > 0) {
            if (checkoutNow) this.goToCheckoutPage();
          }

          else {
            this.noExistingOrderFound = true;
          }
        }

        catch (error) {
          errorHandlers.generic(error);

          this.$store.dispatch('session/updateRouteParamsDirectly', {
            tableNumber: null
          });
        }

        finally {
          this.isFindingOrder = false;
        }
      },

      goToCheckoutPage() {
        this.$closeModal('cart');

        this.$router.push({
          name: 'payAtTableCheckout',
          params: {
            merchantId: this.$route.params.merchantId
          },
          query: {
            ml: this.session.route.merchantLocationId,
            oi: this.order.orderId,
            tb: this.session.route.tableNumber
          }
        });
      },

      async submitPaymentInfo({ contactInfo, paymentMethod }) {
        // set manually entered table number and fetch/begin order at that table
        if (this.manuallyEnteredTableNumber) {
          await this.findOrder({ checkoutNow: false });
        }

        if (this.order.payments.length) {
          events.emit('notification', {
            type: 'error',
            message: `A tab has already been started at this ${this.userLocationType.description.toLowerCase()}`,
            clickToDismiss: true
          });
        }
        else {
          // authorize the payment method with a hold
          await pay.addPayment({
            contactInfo,
            paymentMethod,
            order: this.order,
            total: this.tabHoldAmount,
            completePayment: false // Don't complete the payment yet since this //
            // is just a hold. The payment will be updated with the final order //
            // amounts (total, tip, tax) and completed once the tab is closed   //
          });

          // send the items to the kitchen
          await this.submitOrder();
        }
      },

      async submitOrder() {
        const redirectToMenuPage = this.merchant.features['order-at-table-redirect-to-checkout-disabled'];

        try {
          this.isSubmittingOrder = true;

          if (this.manuallyEnteredTableNumber) {
            this.$store.dispatch('session/updateRouteParamsDirectly', {
              tableNumber: this.manuallyEnteredTableNumber
            });
          }

          // check to see if the order contains any items yet before sending order to kitchen
          const isFirstOrderSubmit = !this.order.orderItems.length;

          await this.$store.dispatch('orders/sendStagedItemsToKitchen');

          // this.$triggerGoogleTrackingEvent('SendOrderToKitchen', 'Order');

          this.$triggerNewRelicEvent('SendOrderToKitchen', 'Information', 'Sent Order To Kitchen');

          if (this.supportsOpenTab) {
            if (isFirstOrderSubmit) {
              // prompt user to checkout now (close tab) or leave tab open
              this.$openModal('openTabFinishOrdering');
            }
            else {
              // return to menu
              this.$closeAllModals();
            }
          }
          else if (redirectToMenuPage) {
            this.$closeModal('cart');
          }
          else {
            this.goToCheckoutPage();
          }
        }

        catch (error) {
          errorHandlers.generic(error);

          this.$store.dispatch('session/updateRouteParamsDirectly', {
            tableNumber: null
          });
        }

        finally {
          this.isSubmittingOrder = false;
        }
      }
    }
  };
</script>
